import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { printDocument } from "@/lib/print";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PatientPicker } from "@/components/opd/PatientPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Printer, Loader2, Receipt, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function OpdBilling() {
  const { activeId, has } = useFacility();
  const [params] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patient, setPatient] = useState(null);
  const [visitId, setVisitId] = useState(null);
  const [items, setItems] = useState([{ description: "Consultation fee", qty: 1, unit_price: 500 }]);
  const [discount, setDiscount] = useState(0);
  const [taxPercent, setTaxPercent] = useState(0);
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [paid, setPaid] = useState(true);

  const load = useCallback(() => {
    if (!activeId) return;
    setRows(null);
    api.get(`/opd/invoices?facility_id=${activeId}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [activeId]);
  useEffect(load, [load]);

  // Prefill from queue "Create invoice" action
  useEffect(() => {
    const pid = params.get("patient");
    if (pid && has("opd_billing:create")) {
      api.get(`/opd/patients/${pid}`).then((r) => setPatient(r.data)).catch(() => {});
      setVisitId(params.get("visit"));
      const fee = parseFloat(params.get("fee") || 500);
      setItems([{ description: "Consultation fee", qty: 1, unit_price: fee || 500 }]);
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subtotal = items.reduce((s, i) => s + (parseFloat(i.qty || 0) * parseFloat(i.unit_price || 0)), 0);
  const taxable = Math.max(0, subtotal - parseFloat(discount || 0));
  const tax = taxable * parseFloat(taxPercent || 0) / 100;
  const total = taxable + tax;

  const setItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addItem = () => setItems([...items, { description: "", qty: 1, unit_price: 0 }]);
  const delItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const reset = () => { setPatient(null); setVisitId(null); setItems([{ description: "Consultation fee", qty: 1, unit_price: 500 }]); setDiscount(0); setTaxPercent(0); setPaid(true); };

  const save = async () => {
    if (!patient) { toast.error("Select a patient"); return; }
    setSaving(true);
    try {
      const payload = { patient_id: patient.id, visit_id: visitId,
        items: items.map((i) => ({ ...i, amount: parseFloat(i.qty || 0) * parseFloat(i.unit_price || 0) })),
        discount: parseFloat(discount || 0), tax_percent: parseFloat(taxPercent || 0), payment_mode: paymentMode, paid };
      const { data } = await api.post("/opd/invoices", payload);
      toast.success(`Invoice ${data.invoice_number} created`);
      setOpen(false); reset(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const markPaid = async (inv) => { try { await api.patch(`/opd/invoices/${inv.id}/pay`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const printInvoice = async (inv) => {
    const { data } = await api.get(`/opd/invoices/${inv.id}`);
    const rowsHtml = data.items.map((i) => `<tr><td>${i.description}</td><td>${i.qty}</td><td>₹${(i.unit_price||0).toLocaleString()}</td><td>₹${((i.qty||1)*(i.unit_price||0)).toLocaleString()}</td></tr>`).join("");
    printDocument(data.invoice_number, `
      <div class="row"><div><h1>${data.facility?.name || "Invoice"}</h1><div class="muted">${data.facility?.address || ""}</div></div>
      <div style="text-align:right"><strong>${data.invoice_number}</strong><div class="muted">${data.invoice_date}</div></div></div>
      <hr class="hr"/>
      <div class="row"><div><div class="muted">Billed to</div><strong>${data.patient_name}</strong><div class="muted">${data.uhid}</div></div>
      <div class="muted">Payment: ${data.payment_mode} · ${data.status.toUpperCase()}</div></div>
      <table><thead><tr><th>Description</th><th>Qty</th><th>Rate</th><th>Amount</th></tr></thead><tbody>${rowsHtml}</tbody></table>
      <div class="totals">
        <div><span>Subtotal</span><span>₹${data.subtotal.toLocaleString()}</span></div>
        <div><span>Discount</span><span>- ₹${data.discount.toLocaleString()}</span></div>
        <div><span>Tax (${data.tax_percent}%)</span><span>₹${data.tax.toLocaleString()}</span></div>
        <div class="grand"><span>Total</span><span>₹${data.total.toLocaleString()}</span></div>
      </div>`);
  };

  return (
    <div data-testid="opd-billing-page">
      <PageHeader title="OPD Billing" subtitle="Generate invoices and receipts for consultations and services."
        actions={has("opd_billing:create") && <Button onClick={() => { reset(); setOpen(true); }} className="bg-blue-700 hover:bg-blue-800" data-testid="new-invoice-button"><Plus className="h-4 w-4 mr-1.5" /> New Invoice</Button>} />

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Invoice</TableHead><TableHead>Patient</TableHead><TableHead>Date</TableHead><TableHead>Amount</TableHead><TableHead>Mode</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((inv) => (
                <TableRow key={inv.id} className="hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                  <TableCell className="font-mono text-xs font-semibold text-blue-700">{inv.invoice_number}</TableCell>
                  <TableCell><div className="font-medium text-slate-800">{inv.patient_name}</div><div className="text-xs text-slate-400 font-mono">{inv.uhid}</div></TableCell>
                  <TableCell className="text-sm text-slate-500">{inv.invoice_date}</TableCell>
                  <TableCell className="font-semibold text-slate-800">₹{inv.total.toLocaleString()}</TableCell>
                  <TableCell className="text-sm text-slate-600">{inv.payment_mode}</TableCell>
                  <TableCell><StatusBadge status={inv.status === "paid" ? "active" : "suspended"} label={inv.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {inv.status !== "paid" && has("opd_billing:edit") && <Button variant="ghost" size="sm" onClick={() => markPaid(inv)} data-testid={`pay-${inv.id}`}><CheckCircle2 className="h-4 w-4 mr-1 text-emerald-600" /> Mark paid</Button>}
                      <Button variant="outline" size="sm" onClick={() => printInvoice(inv)} data-testid={`print-invoice-${inv.id}`}><Printer className="h-3.5 w-3.5 mr-1.5" /> Print</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-12">No invoices yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto thin-scroll" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="h-4 w-4" /> New Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-xs">Patient</Label><PatientPicker value={patient} onChange={setPatient} testId="invoice-patient-picker" /></div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label className="text-xs">Line items</Label><Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-item"><Plus className="h-4 w-4 mr-1.5" /> Add item</Button></div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[2fr_0.6fr_1fr_1fr_auto] gap-2 items-center" data-testid={`item-row-${i}`}>
                    <Input placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`item-desc-${i}`} />
                    <Input type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                    <Input type="number" placeholder="Rate" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`item-price-${i}`} />
                    <div className="text-sm text-slate-600 text-right tabular-nums">₹{((it.qty||0)*(it.unit_price||0)).toLocaleString()}</div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => delItem(i)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Discount (₹)</Label><Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="invoice-discount" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Tax (%)</Label><Input type="number" value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)} data-testid="invoice-tax" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Payment mode</Label>
                <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} data-testid="invoice-mode">
                  <option>Cash</option><option>Card</option><option>UPI</option><option>Insurance</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-4 py-3">
              <label className="flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={paid} onCheckedChange={setPaid} data-testid="invoice-paid" /> Mark as paid</label>
              <div className="text-right"><div className="text-xs text-slate-400">Grand total</div><div className="font-display text-2xl font-bold text-slate-900" data-testid="invoice-total">₹{total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving || !patient} className="bg-blue-700 hover:bg-blue-800" data-testid="invoice-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate invoice"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
